import { useEffect, useState } from "react";
import { Layers, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { adminAPI } from "../../lib/api";
import { toast } from "sonner";

export function UgcQueueSettingsCard() {
  const [stats, setStats] = useState(null);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = async () => {
    try {
      const { data } = await adminAPI.getUgcQueueSettings();
      setStats(data);
      setValue(String(data.max_concurrent));
      setLoadError(false);
    } catch {
      setStats(null);
      setLoadError(true);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const n = parseInt(value, 10);
    if (!(n >= 1 && n <= 20)) {
      toast.error("Choose a number between 1 and 20");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminAPI.updateUgcQueueSettings(n);
      setStats(data);
      toast.success(`UGC videos now run ${n} at a time`);
    } catch {
      toast.error("Could not save the queue setting");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="ugc-queue-settings-card" className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2">
        <Layers className="w-4 h-4 text-cyan-400" />
        <span className="text-sm font-medium">UGC queue</span>
      </div>
      <span data-testid="ugc-queue-stats" className="text-xs text-muted-foreground">
        {stats ? `${stats.running} running · ${stats.queued} waiting` : loadError ? "Couldn't load setting" : "Loading..."}
      </span>
      {loadError && (
        <Button data-testid="ugc-queue-retry" size="sm" variant="ghost" onClick={load}>Retry</Button>
      )}
      <div className="flex items-center gap-2 ml-auto">
        <label htmlFor="ugc-max-concurrent" className="text-xs text-muted-foreground">Videos at once</label>
        <Input id="ugc-max-concurrent" data-testid="ugc-max-concurrent-input" type="number" min={1} max={20}
          value={value} onChange={(e) => setValue(e.target.value)} className="w-20 h-8" />
        <Button data-testid="ugc-max-concurrent-save" size="sm" onClick={save} disabled={saving || !stats}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}
